import { useCallback, useEffect, useState } from "react";
import { Users, Plus, Send, Undo2, History, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { SubAgentFormDialog } from "@/components/subagents/SubAgentFormDialog";
import { SubAgentMoveDialog } from "@/components/subagents/SubAgentMoveDialog";
import { SubAgentHistoryDialog } from "@/components/subagents/SubAgentHistoryDialog";

const NAMES = { olaparty: "Qwick Live", trumeet: "Trumeet" };

const SubRow = ({ s, onAct }) => (
  <tr className="border-b border-zinc-100 text-sm" data-testid={`subagent-row-${s.id}`}>
    <td className="px-4 py-3">
      <div className="font-medium text-zinc-900">{s.name}</div>
      <div className="text-xs text-zinc-500">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
    </td>
    <td className="px-4 py-3 text-xs">
      {s.allowed_platforms.map((k) => <span key={k} className="mr-1 inline-block rounded-full bg-zinc-100 px-2 py-0.5">{NAMES[k] || k}</span>)}
      {s.allowed_recipients?.length > 0 && <div className="mt-1 text-zinc-500">Only {s.allowed_recipients.length} allowed ID(s)</div>}
    </td>
    <td className="px-4 py-3 font-mono text-xs" data-testid={`subagent-balance-${s.id}`}>
      {s.allowed_platforms.map((k) => <div key={k}>{NAMES[k] || k}: <strong>{(s.balances?.[k]?.balance ?? 0).toLocaleString()}</strong></div>)}
    </td>
    <td className="px-4 py-3 text-xs tabular-nums">{s.recharge_count}</td>
    <td className="px-4 py-3">
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.active ? "bg-green-100 text-green-800" : "bg-zinc-200 text-zinc-600"}`} data-testid={`subagent-status-${s.id}`}>{s.active ? "Active" : "Blocked"}</span>
    </td>
    <td className="px-4 py-3">
      <div className="flex flex-wrap justify-end gap-1">
        <Button size="sm" className="bg-zinc-900 hover:bg-zinc-800" onClick={() => onAct("send", s)} data-testid={`subagent-send-${s.id}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>
        <Button size="sm" variant="outline" onClick={() => onAct("reclaim", s)} data-testid={`subagent-reclaim-${s.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Take back</Button>
        <Button size="sm" variant="outline" onClick={() => onAct("history", s)} data-testid={`subagent-history-${s.id}`}><History className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={() => onAct("edit", s)} data-testid={`subagent-edit-${s.id}`}><Settings2 className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="ghost" className={s.active ? "text-rose-600" : "text-emerald-700"} onClick={() => onAct("toggle", s)} data-testid={`subagent-toggle-${s.id}`}>{s.active ? "Block" : "Unblock"}</Button>
      </div>
    </td>
  </tr>
);

export default function ResellerSubAgents() {
  const [data, setData] = useState({ subagents: [], my_platforms: [] });
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/reseller/subagents").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load")), []);
  useEffect(() => { load(); }, [load]);

  const onAct = async (kind, s) => {
    if (kind === "toggle") {
      try { await api.patch(`/reseller/subagents/${s.id}`, { active: !s.active }); toast.success(s.active ? "Sub-agent blocked" : "Sub-agent unblocked"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      return;
    }
    setDlg({ kind, s });
  };
  const close = (changed) => { setDlg(null); if (changed) load(); };

  return (
    <div className="space-y-5" data-testid="reseller-subagents-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-zinc-700" /><h1 className="font-display text-xl font-semibold text-zinc-900">Sub-agents</h1></div>
          <p className="text-sm text-zinc-500 mt-1">Create sub-agents, send them diamonds from your stock, and track their recharges.</p>
        </div>
        <Button onClick={() => setDlg({ kind: "create" })} className="bg-zinc-900 hover:bg-zinc-800" data-testid="subagent-create-button"><Plus className="w-4 h-4 mr-1" />Add sub-agent</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>{["Sub-agent", "Platforms", "Balance", "Recharges", "Status", ""].map((h) => <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.subagents.map((s) => <SubRow key={s.id} s={s} onAct={onAct} />)}
            {data.subagents.length === 0 && <tr><td colSpan="6" className="px-4 py-12 text-center text-sm text-zinc-400">No sub-agents yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <SubAgentFormDialog open={dlg?.kind === "create" || dlg?.kind === "edit"} sub={dlg?.kind === "edit" ? dlg.s : null} myPlatforms={data.my_platforms} onClose={close} />
      <SubAgentMoveDialog open={dlg?.kind === "send" || dlg?.kind === "reclaim"} mode={dlg?.kind} sub={dlg?.s} onClose={close} />
      <SubAgentHistoryDialog sub={dlg?.kind === "history" ? dlg.s : null} onClose={() => setDlg(null)} />
    </div>
  );
}
